import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import { AuthFormCardSkeleton } from '@/shared/components/skeletons'
import brushfireLogo from '@/features/auth/assets/login/brushfire-logo.png'
import { signupVerifyLogoBlockSx, signupVerifyLogoImgSx } from '@/features/auth/styles/signupVerifyStyles'
import type { InstrumentLinkOnboardingViewModel } from '../hooks/useInstrumentLinkOnboarding'
import { ConsentPolicyCard } from './ConsentPolicyCard'
import { InstrumentLinkBlockedDialog } from './InstrumentLinkBlockedDialog'
import {
  ONBOARDING_PRIVACY_SUBTITLE,
  ONBOARDING_TERMS_SUBTITLE,
} from '../lib/constants'
import {
  PRIVACY_ACCEPT_LABEL,
  PRIVACY_BODY_PARAGRAPHS,
  PRIVACY_LAST_UPDATED,
  PRIVACY_POLICY_SECTION_TITLE,
  TERMS_ACCEPT_LABEL,
  TERMS_BODY_PARAGRAPHS,
  TERMS_LAST_UPDATED,
  TERMS_POLICY_SECTION_TITLE,
} from '../lib/policy-content'
import {
  instrumentVerifyCardSx,
  instrumentVerifyHeadingSx,
  instrumentVerifySubtitleSx,
  surveyFirstHeadingSx,
  surveyFirstLogoBlockSx,
  surveyFirstLogoImgSx,
  surveyFirstSubtitleSx,
  surveyFirstVerifyCardSx,
} from '../styles/instrumentOnboardingStyles'
import { SurveySignupStepper } from './SurveySignupStepper'
import {
  InstrumentLinkContactStep,
  InstrumentLinkOtpStep,
  VerifyStepHeader,
} from './instrumentLinkVerifyView.helpers'

export function InstrumentLinkInvalidView() {
  return (
    <Box sx={{ width: '100%' }} data-testid="instrument-onboarding-invalid-link">
      <InstrumentLinkBlockedDialog
        open
        headline="Invalid or expired link"
        message="Please contact your administrator for a new link."
      />
    </Box>
  )
}

export function InstrumentLinkLoadingView() {
  return (
    <Box sx={{ width: '100%' }} data-testid="instrument-onboarding-loading">
      <AuthFormCardSkeleton testId="instrument-onboarding-loading-skeleton" />
    </Box>
  )
}

interface InstrumentLinkErrorViewProps {
  readonly message: string
}

export function InstrumentLinkErrorView({ message }: Readonly<InstrumentLinkErrorViewProps>) {
  const isInvalidLink = message.toLowerCase().includes('invalid or expired link')
  return (
    <Box sx={{ width: '100%' }} data-testid="instrument-onboarding-error">
      <InstrumentLinkBlockedDialog
        open
        headline={isInvalidLink ? 'Invalid or expired link' : 'Unable to continue'}
        message={
          isInvalidLink
            ? 'Please contact your administrator for a new link.'
            : message
        }
      />
    </Box>
  )
}

interface InstrumentLinkConsentViewProps {
  readonly step: 'privacy' | 'terms'
  readonly serverError: string | null
  readonly acceptPending: boolean
  readonly showConsentDeclined: boolean
  readonly onAccept: () => void
  readonly onDecline: () => void
  readonly onRetryConsent: () => void
}

export function InstrumentLinkConsentView({
  step,
  serverError,
  acceptPending,
  showConsentDeclined,
  onAccept,
  onDecline,
  onRetryConsent,
}: Readonly<InstrumentLinkConsentViewProps>) {
  const isPrivacy = step === 'privacy'
  const subtitle = isPrivacy ? ONBOARDING_PRIVACY_SUBTITLE : ONBOARDING_TERMS_SUBTITLE

  return (
    <Box sx={{ width: '100%' }}>
      {serverError && (
        <Alert severity="error" sx={{ mb: 2, maxWidth: 672, mx: 'auto' }} role="alert">
          {serverError}
        </Alert>
      )}
      <ConsentPolicyCard
        key={step}
        pageSubtitle={subtitle}
        policyTitle={isPrivacy ? PRIVACY_POLICY_SECTION_TITLE : TERMS_POLICY_SECTION_TITLE}
        bodyParagraphs={isPrivacy ? PRIVACY_BODY_PARAGRAPHS : TERMS_BODY_PARAGRAPHS}
        lastUpdated={isPrivacy ? PRIVACY_LAST_UPDATED : TERMS_LAST_UPDATED}
        acceptLabel={isPrivacy ? PRIVACY_ACCEPT_LABEL : TERMS_ACCEPT_LABEL}
        acceptPending={acceptPending}
        declined={showConsentDeclined}
        onAccept={onAccept}
        onDecline={onDecline}
        onRetryConsent={onRetryConsent}
      />
    </Box>
  )
}

export function InstrumentLinkVerifyView({
  vm,
}: Readonly<{ vm: InstrumentLinkOnboardingViewModel }>) {
  const { step, session, channel, serverError, showOtpOnboarding } = vm

  if (!session) return null

  const showOtpStep = step === 'otp'

  return (
    <Box
      data-testid="instrument-onboarding-page"
      sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
    >
      <Box sx={vm.surveyFirstPhone ? surveyFirstVerifyCardSx : instrumentVerifyCardSx}>
        <Box sx={vm.surveyFirstPhone ? surveyFirstLogoBlockSx : signupVerifyLogoBlockSx}>
          <Box
            component="img"
            src={brushfireLogo}
            alt="Brushfire"
            sx={vm.surveyFirstPhone ? surveyFirstLogoImgSx : signupVerifyLogoImgSx}
          />
        </Box>

        {vm.surveyFirstPhone ? (
          <SurveySignupStepper activeStep={1} compactLabels />
        ) : null}

        <VerifyStepHeader
          showOtpStep={showOtpStep}
          channel={channel}
          headingSx={vm.surveyFirstPhone ? surveyFirstHeadingSx : instrumentVerifyHeadingSx}
          subtitleSx={vm.surveyFirstPhone ? surveyFirstSubtitleSx : instrumentVerifySubtitleSx}
          surveyFirst={vm.surveyFirstPhone}
        />

        {!showOtpStep && showOtpOnboarding ? (
          <InstrumentLinkContactStep vm={vm} session={session} />
        ) : null}
        {showOtpStep ? <InstrumentLinkOtpStep vm={vm} /> : null}

        {serverError && !showOtpStep && step === 'channel' && (
          <Alert severity="error" role="alert" sx={{ width: '100%' }}>
            {serverError}
          </Alert>
        )}
      </Box>
    </Box>
  )
}
